import { useCallback, useMemo, useState } from "react";
import {
  Box,
  EditableValueCell,
  Text,
  type EditableValueCellAction,
  type EditableValueCellEditor,
  type Omittable,
} from "@salestrak/ui";
import { AddressPropertyInput } from "#app/components/issue-view/address-property-input";
import {
  buildCopyValueAction,
  getFieldTypeDescriptor,
} from "#app/components/fields/field-type-descriptors";

/**
 * The built-in Address PROPERTY cell: the same `EditableValueCell`
 * composition a custom field of type `address` gets — same activation, same
 * touch reveal, same hover value-actions row, same outside-click dismissal —
 * reading its action set and noun from the `address` descriptor so a table
 * showing both an address property column and an address custom-field column
 * cannot present two editing UXs. Only the commit differs: the property
 * writes through the issue's address mutation, not the values map.
 *
 * The cell owns its activation/highlight state — table cells have no
 * dialog-style container tracking rows.
 */

export type AddressValueCellProps = Omittable<{
  wholeAddress?: string | null;
  onSelect: (wholeAddress: string) => void;
  onClear: () => void;
  /** View/comment access tiers: display + copy only. */
  readOnly?: boolean;
}>;

export function AddressValueCell({
  wholeAddress,
  onSelect,
  onClear,
  readOnly = false,
}: AddressValueCellProps) {
  const [isActive, setIsActive] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);

  const descriptor = getFieldTypeDescriptor("address");
  const hasValue = !!wholeAddress;

  // Commits arrive from the clear action (`overwrite(null)`); selections go
  // straight through `onSelect` in the editor below.
  const handleCommit = useCallback(
    (next: unknown) => {
      if (typeof next === "string" && next.trim()) onSelect(next);
      else onClear();
    },
    [onClear, onSelect],
  );

  const actions = useMemo<EditableValueCellAction[]>(() => {
    if (!hasValue) return [];
    const noun = descriptor.actionNoun;
    const list: EditableValueCellAction[] = [];
    // Derived from the descriptor's action set, in its order, so this cell and
    // an address custom-field cell always offer the same actions. `open` and
    // `fullscreen` have no property-address behavior and are skipped.
    for (const action of descriptor.actions) {
      switch (action) {
        case "copy":
          list.push(buildCopyValueAction(noun, () => wholeAddress ?? null));
          break;
        case "clear":
          list.push({
            key: "clear",
            icon: "x",
            label: `Clear ${noun}`,
            onSelect: (cell) => cell.overwrite(null),
          });
          break;
      }
    }
    return readOnly ? list.filter((a) => a.key === "copy") : list;
  }, [descriptor, hasValue, readOnly, wholeAddress]);

  const display = (
    <Box fill="flex" direction="row" align="center" gap="xs" paddingX="md">
      <Box fill="flex">
        <Text
          size="sm"
          variant={hasValue ? undefined : "muted"}
          truncate={1}
        >
          {wholeAddress || "-"}
        </Text>
      </Box>
    </Box>
  );

  // Same shape as the custom-field `address` editor: activation mounts the
  // autocomplete input auto-opened; closing its popover deactivates.
  const editor: EditableValueCellEditor = {
    kind: "inline",
    render: ({ deactivate }) => (
      <AddressPropertyInput
        variant="table"
        wholeAddress={wholeAddress}
        onSelect={(address) => {
          onSelect(address);
          deactivate();
        }}
        onClear={onClear}
        autoOpen
        onOpenChange={(open) => {
          if (!open) deactivate();
        }}
      />
    ),
  };

  return (
    <EditableValueCell
      value={wholeAddress ?? null}
      display={display}
      hasValue={hasValue}
      editor={readOnly ? undefined : editor}
      triggerLabel={`Edit ${descriptor.actionNoun}`}
      // A table cell hard-clips: the row-style end bleed would cut the
      // outline's corners square.
      bleedEnd={false}
      readOnly={readOnly}
      actions={actions}
      onCommit={handleCommit}
      isActive={isActive}
      onActivate={() => setIsActive(true)}
      onDeactivate={() => setIsActive(false)}
      highlighted={overlayOpen || isActive}
      onOverlayChange={setOverlayOpen}
    />
  );
}
